import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type PaymentMethodKind, type ProviderPreview, type ProviderRule } from '../api';
import { date, money, type Dictionary, type Locale } from '../i18n';
import * as Icon from '@firmeto/design/Icons';

/**
 * Operatorzy płatności (decyzja z 31.10.2026): którzy, dla jakiej formy płatności, kiedy i za ile.
 *
 * Z kosztu wynika operator domyślny dla klienta — najtańszy dla nas przy jego zwykłej kwocie.
 * Podgląd pokazuje to od razu: przy 60 zł wygrywa niska opłata stała, przy 3 000 zł niski procent.
 */
const METHODS: PaymentMethodKind[] = ['CARD', 'BLIK'];

export function Providers({ t, locale }: { t: Dictionary; locale: Locale }) {
  const [providers, setProviders] = useState<string[]>([]);
  const [rules, setRules] = useState<ProviderRule[] | null>(null);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ provider: '', method: 'CARD' as PaymentMethodKind, from: today, to: '', percent: '', fixed: '', note: '' });
  const [preview, setPreview] = useState<ProviderPreview | null>(null);
  const [check, setCheck] = useState({ method: 'CARD' as PaymentMethodKind, amount: '60.27' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const loaded = await api.providerRules();
      setProviders(loaded.providers);
      setRules(loaded.rules);
      setForm((current) => (current.provider ? current : { ...current, provider: loaded.providers[0] ?? '' }));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(action: () => Promise<unknown>, after?: () => void) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await action();
      after?.();
      setSaved(true);
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const decimal = (value: string) => value.replace(',', '.').trim() || '0';

  return (
    <>
      <div className="hd">
        <div>
          <h3>{t.providers.title}</h3>
          <p>{t.providers.lead}</p>
        </div>
      </div>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}
      {saved && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{t.providers.saved}</b>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="hd">
          <div>
            <h4>{t.providers.rules}</h4>
          </div>
        </div>
        {rules === null ? (
          <p className="muted">{t.loading}</p>
        ) : rules.length === 0 ? (
          <p className="muted">{t.providers.none}</p>
        ) : (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.providers.method}</th>
                  <th>{t.providers.provider}</th>
                  <th>{t.providers.from}</th>
                  <th>{t.providers.to}</th>
                  <th className="n">{t.providers.fee}</th>
                  <th className="wrap">{t.providers.note}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td>{t.providers.methods[rule.method] ?? rule.method}</td>
                    <td>
                      <b>{rule.provider}</b>{' '}
                      <span className={rule.active ? 'chip st-ok' : 'chip st-draft'}>{rule.active ? t.providers.active : t.providers.inactive}</span>
                    </td>
                    <td>{date(rule.validFrom, locale)}</td>
                    <td>{rule.validTo ? date(rule.validTo, locale) : '—'}</td>
                    <td className="n mono">
                      {rule.feePercent}% + {money(rule.feeFixed, locale)}
                    </td>
                    <td className="wrap">{rule.note ?? ''}</td>
                    <td>
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={busy}
                        onClick={() => void act(() => api.changeProviderRule(rule.id, !rule.active, rule.validTo))}
                      >
                        {rule.active ? t.providers.disable : t.providers.enable}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid two">
        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void act(
              () =>
                api.createProviderRule({
                  provider: form.provider,
                  method: form.method,
                  validFrom: form.from,
                  validTo: form.to || null,
                  feePercent: decimal(form.percent),
                  feeFixed: decimal(form.fixed),
                  note: form.note.trim() || null,
                }),
              () => setForm({ ...form, percent: '', fixed: '', note: '', to: '' }),
            );
          }}
        >
          <div className="hd">
            <div>
              <h4>{t.providers.add}</h4>
              <p>{t.providers.addLead}</p>
            </div>
          </div>
          <div className="row">
            <label className="fld">
              <span>{t.providers.provider}</span>
              <select className="inp" value={form.provider} onChange={(event) => setForm({ ...form, provider: event.target.value })}>
                {providers.map((provider) => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
            </label>
            <label className="fld">
              <span>{t.providers.method}</span>
              <select className="inp" value={form.method} onChange={(event) => setForm({ ...form, method: event.target.value as PaymentMethodKind })}>
                {METHODS.map((method) => (
                  <option key={method} value={method}>
                    {t.providers.methods[method]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="row">
            <label className="fld">
              <span>{t.providers.from}</span>
              <input className="inp mono" type="date" min={today} value={form.from} onChange={(event) => setForm({ ...form, from: event.target.value })} required />
            </label>
            <label className="fld">
              <span>{t.providers.to}</span>
              <input className="inp mono" type="date" value={form.to} onChange={(event) => setForm({ ...form, to: event.target.value })} />
            </label>
          </div>
          <div className="row">
            <label className="fld">
              <span>{t.providers.percent}</span>
              <input className="inp mono" inputMode="decimal" placeholder="1,29" value={form.percent} onChange={(event) => setForm({ ...form, percent: event.target.value })} />
            </label>
            <label className="fld">
              <span>{t.providers.fixed}</span>
              <input className="inp mono" inputMode="decimal" placeholder="0,30" value={form.fixed} onChange={(event) => setForm({ ...form, fixed: event.target.value })} />
            </label>
          </div>
          <label className="fld">
            <span>{t.providers.note}</span>
            <input className="inp" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
          </label>
          <div className="row">
            <button type="submit" className="btn" disabled={busy || !form.provider}>
              {t.providers.save}
            </button>
          </div>
        </form>

        <form
          className="panel"
          onSubmit={(event) => {
            event.preventDefault();
            void act(async () => setPreview(await api.previewProviders(check.method, decimal(check.amount))));
          }}
        >
          <div className="hd">
            <div>
              <h4>{t.providers.preview}</h4>
            </div>
          </div>
          <div className="row">
            <label className="fld">
              <span>{t.providers.method}</span>
              <select className="inp" value={check.method} onChange={(event) => setCheck({ ...check, method: event.target.value as PaymentMethodKind })}>
                {METHODS.map((method) => (
                  <option key={method} value={method}>
                    {t.providers.methods[method]}
                  </option>
                ))}
              </select>
            </label>
            <label className="fld">
              <span>{t.providers.amount}</span>
              <input className="inp mono" inputMode="decimal" value={check.amount} onChange={(event) => setCheck({ ...check, amount: event.target.value })} />
            </label>
            <button type="submit" className="btn ghost" disabled={busy}>
              {t.providers.check}
            </button>
          </div>
          {preview &&
            (preview.options.length === 0 ? (
              <p className="muted">{t.providers.nobody}</p>
            ) : (
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th>{t.providers.provider}</th>
                      <th className="n">{t.providers.cost}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.options.map((option) => (
                      <tr key={option.provider}>
                        <td>
                          <b>{option.provider}</b> {option.default && <span className="chip st-ok">{t.providers.isDefault}</span>}
                        </td>
                        <td className="n mono">{money(option.costGross, locale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
        </form>
      </div>
    </>
  );
}
